import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { api } from "../lib/api";
import { decimalCents, formatCents, voucherNumber } from "../lib/format";
import type { VoucherView } from "../lib/hooks";
import { Dialog } from "./period-picker";
import { Button, ErrorBox, Field, Input } from "./ui";

interface BulkVoidResult {
  allowed: number;
  blocked: { voucherId: string; voucher: string; problem: string }[];
  token: string;
}

/**
 * Voids or reactivates vouchers, with a reason (plan 024: nothing is deleted). The preview says
 * which cannot and why; the change carries the preview's token, so it does nothing if the list
 * changed in between. With some blocked, the user can go on with the rest: a new preview of those,
 * seen before confirming (Codex 024).
 */
export function VoidDialog({
  companyId,
  vouchers,
  action,
  onClose,
  onDone,
}: {
  companyId: string;
  vouchers: VoucherView[];
  action: "void" | "unvoid";
  onClose: () => void;
  onDone: () => void;
}) {
  const client = useQueryClient();
  const [reason, setReason] = useState("");
  // Going on with the rest asks for a new preview of just those: the user sees what is confirmed.
  const [ids, setIds] = useState(() => vouchers.map((v) => v.id));
  const url = `/api/companies/${companyId}/vouchers/${action}`;
  const preview = useQuery({
    queryKey: ["bulk-void-preview", companyId, action, ids],
    queryFn: () => api.post<BulkVoidResult>(url, { voucherIds: ids, dryRun: true }),
    retry: false,
  });
  const blocked = new Set(preview.data?.blocked.map((b) => b.voucherId));
  const going = ids.filter((id) => !blocked.has(id));
  const change = useMutation({
    mutationFn: () =>
      api.post(url, { voucherIds: ids, reason, dryRun: false, token: preview.data?.token }),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["vouchers", companyId] });
      await client.invalidateQueries({ queryKey: ["periods", companyId] });
      await client.invalidateQueries({ queryKey: ["caja", companyId] });
      await client.invalidateQueries({ queryKey: ["books", companyId] });
      onDone();
    },
  });
  const voiding = action === "void";
  const verb = voiding ? "Anular" : "Reactivar";
  const one = vouchers.length === 1 ? vouchers[0] : undefined;
  return (
    <Dialog
      title={
        one
          ? `${verb} ${one.typeName} ${voucherNumber(one.pointOfSale, one.number)}`
          : `${verb} ${ids.length} comprobantes`
      }
      onClose={onClose}
    >
      <div className="flex flex-col gap-3">
        <p className="text-sm">
          {one ? (
            <>
              {one.counterpart.name} por {formatCents(decimalCents(one.total))}.{" "}
            </>
          ) : null}
          {voiding
            ? "No se borra: queda en su período, tachado y con el motivo, y deja de contar en la Caja IVA y los libros. Se puede reactivar."
            : "Vuelve a contar en la Caja IVA y los libros de su período."}
        </p>
        <Field label="Motivo (obligatorio)">
          <Input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={
              voiding ? "Ej.: cargado dos veces por error" : "Ej.: se anuló por equivocación"
            }
          />
        </Field>
        <ErrorBox error={preview.error} />
        {preview.data && preview.data.blocked.length > 0 ? (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm">
            <p className="font-medium">
              {preview.data.blocked.length === 1
                ? `Este no se puede ${verb.toLowerCase()}:`
                : `Estos ${preview.data.blocked.length} no se pueden ${verb.toLowerCase()}:`}
            </p>
            <ul className="mt-1 list-disc pl-5">
              {preview.data.blocked.map((b) => (
                <li key={b.voucherId}>
                  {b.voucher}: {b.problem}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        <ErrorBox error={change.error} />
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          {preview.data && going.length < ids.length ? (
            <Button variant="secondary" disabled={going.length === 0} onClick={() => setIds(going)}>
              {going.length === 0
                ? `Ninguno se puede ${verb.toLowerCase()}`
                : `Seguir solo con los ${going.length} que se pueden`}
            </Button>
          ) : (
            <Button
              variant={voiding ? "danger" : "primary"}
              disabled={!preview.data || reason.trim().length < 5 || change.isPending}
              onClick={() => change.mutate()}
            >
              {ids.length === 1 ? verb : `${verb} ${ids.length}`}
            </Button>
          )}
        </div>
      </div>
    </Dialog>
  );
}
